import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API, formatApiError } from "@/lib/api";

function Panel({ title, columns, data, csvHref, testidPrefix }) {
  return (
    <section className="adm-card mb-8 overflow-hidden" data-testid={`${testidPrefix}-panel`}>
      <header className="flex items-center justify-between px-6 py-4 border-b border-[#EFECE4]">
        <div>
          <h2 className="text-[15px] font-semibold text-neutral-900">{title}</h2>
          <p className="text-xs text-neutral-400 mt-0.5">{data.length} entr{data.length === 1 ? "y" : "ies"}</p>
        </div>
        <a
          href={csvHref}
          className="adm-btn adm-btn-sm"
          data-testid={`${testidPrefix}-csv`}
          rel="noreferrer"
        >
          Download CSV
        </a>
      </header>

      <div className="overflow-x-auto">
        <table className="adm-table">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 && (
              <tr><td colSpan={columns.length} className="px-4 py-6 text-center text-neutral-500 text-xs">No entries.</td></tr>
            )}
            {data.map((row, i) => (
              <tr key={i} data-testid={`${testidPrefix}-row-${i}`}>
                {columns.map((c) => (
                  <td key={c.key} className="align-top max-w-md">
                    <div className="line-clamp-3 break-words">{row[c.key] ?? ""}</div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function SignupsAdmin() {
  const [ea, setEa] = useState([]);
  const [pi, setPi] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [ea1, pi1] = await Promise.all([
        api.get("/admin/signups/early-access"),
        api.get("/admin/signups/partner-inquiries"),
      ]);
      setEa(ea1.data);
      setPi(pi1.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div data-testid="signups-admin">
      <header className="mb-7">
        <p className="adm-eyebrow mb-1.5">Nettside</p>
        <h1 className="adm-title">Signups</h1>
        <p className="adm-sub mt-1.5">Early Access list and partner inquiries. Export as CSV.</p>
      </header>

      {loading ? (
        <p className="text-sm text-neutral-500">Loading…</p>
      ) : (
        <>
          <Panel
            title="Early Access"
            columns={[
              { key: "created_at", label: "Created" },
              { key: "full_name", label: "Name" },
              { key: "email", label: "Email" },
              { key: "company", label: "Company" },
              { key: "role", label: "Role" },
            ]}
            data={ea}
            csvHref={`${API}/admin/signups/early-access.csv`}
            testidPrefix="signups-ea"
          />
          <Panel
            title="Partner inquiries"
            columns={[
              { key: "created_at", label: "Created" },
              { key: "name", label: "Name" },
              { key: "email", label: "Email" },
              { key: "company", label: "Company" },
              { key: "message", label: "Message" },
            ]}
            data={pi}
            csvHref={`${API}/admin/signups/partner-inquiries.csv`}
            testidPrefix="signups-pi"
          />
        </>
      )}
    </div>
  );
}
